'use client'
import { BlogListPage, BlogNavigation as BlogNavigationType } from 'cms-types'
import { useRouter } from 'next/navigation'
import { useCallback, useRef, useState } from 'react'
import { BlogGrid, BlogPostCardType, type LoadableComponentProps, LoadingText } from 'ui'

import { SortingType } from '@/app/[locale]/hautwissen/[slug]/page'
import { resolveRelation } from '@/cms/helpers'

type BlogPageProps = LoadableComponentProps<{
  blogNavigation: BlogNavigationType
  page: BlogListPage
  fetchPosts: (
    currentPage: number,
    sorting: 'new' | 'old',
  ) => Promise<{
    items: BlogPostCardType[]
    totalPages: number
  }>
  firstPosts?: BlogPostCardType[]
  totalPagesCount: number
}>

function BlogPage({ data, loading }: BlogPageProps) {
  const router = useRouter()
  const teaser = data?.page?.teaser?.at(0)

  const currentPage = useRef(2)
  const totalPages = useRef(data?.totalPagesCount)

  const [sorting, setSorting] = useState<SortingType>('new')
  const [posts, setPosts] = useState<BlogPostCardType[]>(data?.firstPosts || [])
  const [isFetching, setIsFetching] = useState(false)

  const addNewPosts = useCallback(
    async (sort: SortingType, dropPostsList?: boolean) => {
      if (!data || !totalPages.current || isFetching || currentPage.current > totalPages.current)
        return

      setIsFetching(true)

      const { items, totalPages: responseTotalPages } = await data.fetchPosts(
        currentPage.current,
        sort,
      )

      setPosts(prev => (dropPostsList ? [...items] : [...prev, ...items]))

      currentPage.current += 1
      totalPages.current = responseTotalPages

      setIsFetching(false)
    },
    [data, isFetching],
  )

  if (loading) {
    return (
      <>
        <div className='mt-[-2rem] pl-5 sm:mt-[-2.5rem] xl:mt-[-3rem] xl:flex xl:w-full xl:flex-row xl:justify-end xl:px-10'>
          <LoadingText className='sm w-[12%] whitespace-nowrap pt-8 xl:px-5 xl:pt-0' />
        </div>
        <BlogGrid loading={loading} />
      </>
    )
  }

  const onChangeSorting = () => {
    currentPage.current = 1

    setSorting(prev => {
      const newSort = prev === 'new' ? 'old' : 'new'

      addNewPosts(newSort, true)

      return newSort
    })
  }

  const { sortParams } = data.blogNavigation

  return (
    <>
      {sortParams?.enabled && (
        <div className='mt-[-2rem] pl-5 sm:mt-[-2.5rem] xl:mt-[-3rem] xl:flex xl:w-full xl:flex-row xl:justify-end xl:px-10'>
          <p className='sm whitespace-nowrap pt-8 xl:px-5 xl:pt-0'>
            {`${sortParams.text} `}

            <button
              type='button'
              onClick={onChangeSorting}
              className='underline underline-offset-4'
            >
              {sorting === 'new' ? sortParams.newestTitle : sortParams.oldestTitle}
            </button>
          </p>
        </div>
      )}
      <BlogGrid
        data={{
          priority: true,
          posts,
          suggestionCard: teaser && { ...teaser, image: resolveRelation(teaser?.image) },
          onScrollToEnd: () => addNewPosts(sorting),
          isInfiniteScrollLoading: isFetching,
          onCardClick: (slug: string) => router.push('/post/' + slug),
        }}
      />
    </>
  )
}

export default BlogPage
